import {Controller, useFormContext} from 'react-hook-form';
import {Box, Typography, Divider} from '@mui/material';
import './Mp3Components.scss';
import {ArrToggle} from '../../../../common/arr-toggle/ArrToggle';
import ArrRadio from '../../../../common/arr-radio/ArrRadio';
import TagsSection from '../../common/tags-section/TagsSection';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {
  GetMp3FileComponentRequestParam,
  GetTemplateDetailsRequestParam,
  TagItem,
} from '../../../../../api/types/production.type';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {useTranslation} from 'react-i18next';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {useDispatch, useSelector} from 'react-redux';
import {
  selectFillerAudioFileList,
  selectProductionRedactionTagList,
  selectSecurityGroupId,
} from '../../../../../redux/slice/productionSelectors';
import {useEffect, useMemo, useRef, useState} from 'react';
import {
  useGetMp3FileComponent,
  useGetTemplateMp3FileComponent,
} from '../../../../../hooks/productions/useProductions';
import {FormMode, ModuleType} from '../../CreateNewProductionUtils';
import {transformTagTreeData} from '../../CreateNewProductionHelpers';
import {setIsFieldsChanged} from '../../../../../redux/slice/productionSlice';

interface Mp3ComponentsProps {
  isMP3FileSelected: boolean;
  productionId: number;
  isProductionSet: boolean;
  mode?: FormMode;
  isTemplate?: boolean;
  templateId?: number;
}

const Mp3Components: React.FC<Mp3ComponentsProps> = ({
  productionId,
  isProductionSet,
  mode = FormMode.CREATE,
  isTemplate = false,
  templateId,
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const isViewMode = mode === FormMode.VIEW;
  const mp3ComponentRef = useRef<HTMLDivElement | null>(null);
  const mp3ViewModeFocusRingTargetsRef = useRef<
    Array<{
      element: HTMLElement;
      focusHandler: EventListener;
      blurHandler: EventListener;
    }>
  >([]);
  const {control, watch, setValue, reset} = useFormContext();

  const securityGroupId = useSelector(selectSecurityGroupId);
  const fillerAudioFileList = [
    {label: 0, value: 'Select'}, // default option
    ...useSelector(selectFillerAudioFileList).map((item: any) => ({
      value: item.text,
      label: Number(item.value),
    })),
  ];

  const redactionTagList = useSelector(selectProductionRedactionTagList);
  const [TagItem, setTagItem] = useState<TagItem[]>([]);
  const [componentIsDirty, setComponentIsDirty] = useState<boolean>(false);

  // Determine moduleType from isProductionSet using enum
  const moduleType = useMemo(
    () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
    [isProductionSet]
  );

  const {
    data: initialMP3DataFromProduction,
    isLoading: isLoadingFromProduction,
  } = useGetMp3FileComponent(
    {
      SecurityGroupId: securityGroupId,
      moduletype: moduleType,
      productionId: productionId,
      exportProductionId: 0,
    } as GetMp3FileComponentRequestParam,
    !!securityGroupId && !isTemplate && productionId > 0
  );

  const {data: initialMP3DataFromTemplate, isLoading: isLoadingFromTemplate} =
    useGetTemplateMp3FileComponent(
      {
        templateId: templateId!,
        securityGroupId: securityGroupId,
      } as GetTemplateDetailsRequestParam,
      Boolean(!!templateId && isTemplate)
    );

  // Use the appropriate data based on isTemplate flag
  const initialMP3Data = isTemplate
    ? initialMP3DataFromTemplate
    : initialMP3DataFromProduction;
  const isLoading = isTemplate
    ? isLoadingFromTemplate
    : isLoadingFromProduction;

  useEffect(() => {
    if (componentIsDirty) {
      dispatch(setIsFieldsChanged({isFieldsChanged: true}));
    }
  }, [componentIsDirty]);

  useEffect(() => {
    if (initialMP3Data) {
      const initialRedactionStyle =
        fillerAudioFileList.find(
          (item) => item.label === Number(initialMP3Data?.fillerAudioID)
        ) || null;
      const resetValues = {
        isBurnRedactionSelected:
          initialMP3Data.isBurnRedactionSelected ?? false,
        isRedactionByAnnotation:
          initialMP3Data.isRedactionByAnnotation ?? false,
        redactionType: String(initialMP3Data.redactionType ?? '0'),
        redactionStyle: initialRedactionStyle ?? {value: '', label: 0},
        produceLoadFile: initialMP3Data.produceLoadFile ?? false,
        specifiedRedactionTagList:
          initialMP3Data.specifiedRedactionTagList ?? '',
      };

      Object.entries(resetValues).forEach(([key, value]) => {
        setValue(`mP3ComponentModelData.${key}`, value);
      });

      if (initialMP3Data.specifiedRedactionTagList) {
        const selectedIds = initialMP3Data.specifiedRedactionTagList.split(',');
        const tagsArray = redactionTagList
          .filter((tag) => selectedIds.includes(tag.id))
          .map((tag) => ({
            tagId: Number(tag.id),
            tagLabel: tag.text,
          }));
        setTagItem(tagsArray);
      }
    }
  }, [initialMP3Data, reset]);

  const redactionType = watch('mP3ComponentModelData.redactionType');
  const isBurnRedactionSelected = watch(
    'mP3ComponentModelData.isBurnRedactionSelected'
  );
  const produceMP3LoadFile = watch('mP3ComponentModelData.produceLoadFile');
  const redactionStyle = watch('mP3ComponentModelData.redactionStyle');
  useEffect(() => {
    if (!isViewMode) {
      return;
    }

    let animationFrameId = 0;

    const getControlText = (element: HTMLElement): string =>
      (element.getAttribute('aria-label') || element.textContent || '')
        .trim()
        .replace(/\s+/g, ' ');

    const setViewModeNonFocusable = (
      element: HTMLElement | null | undefined
    ) => {
      if (!element) {
        return;
      }
      element.setAttribute('tabindex', '-1');
    };

    const setViewModeFocusable = (
      element: HTMLElement | null | undefined,
      ariaLabel?: string
    ) => {
      if (!element) {
        return;
      }

      // Keep each selected value as a single tab stop.
      element
        .querySelectorAll<HTMLElement>(
          'button, input, select, textarea, a[href], [role="button"], [role="radio"], [role="checkbox"], [role="switch"], [tabindex]'
        )
        .forEach((nestedElement) => {
          if (nestedElement !== element) {
            setViewModeNonFocusable(nestedElement);
          }
        });

      element.setAttribute('tabindex', '0');
      element.setAttribute('aria-disabled', 'true');
      if (ariaLabel) {
        element.setAttribute('aria-label', ariaLabel);
      }

      const hasExistingFocusHandler =
        mp3ViewModeFocusRingTargetsRef.current.some(
          (entry) => entry.element === element
        );

      if (!hasExistingFocusHandler) {
        const focusHandler = () => {
          element.style.outline = '2px solid #fff';
          element.style.outlineOffset = '2px';
          element.style.borderRadius = element.style.borderRadius || '4px';
        };
        const blurHandler = () => {
          element.style.outline = '';
          element.style.outlineOffset = '';
        };

        element.addEventListener('focus', focusHandler);
        element.addEventListener('blur', blurHandler);
        mp3ViewModeFocusRingTargetsRef.current.push({
          element,
          focusHandler,
          blurHandler,
        });
      }
    };

    const suppressDropdownWrapperFocus = (dropdownChild: HTMLElement) => {
      const wrapper = dropdownChild.closest<HTMLElement>(
        '[data-testid^="wrapper-"]'
      );
      const selectDisplay = dropdownChild.closest<HTMLElement>(
        '[data-testid^="arr-select-dropdown-"]'
      );
      const combobox = dropdownChild.closest<HTMLElement>(
        '[role="combobox"][data-testid]'
      );

      setViewModeNonFocusable(wrapper);
      setViewModeNonFocusable(selectDisplay);
      setViewModeNonFocusable(combobox);

      selectDisplay
        ?.querySelectorAll<HTMLElement>('input, [tabindex], [role="combobox"]')
        .forEach((nestedElement) => {
          if (
            !nestedElement.matches('[data-testid^="selected-item-tooltip-"]')
          ) {
            setViewModeNonFocusable(nestedElement);
          }
        });
    };

    const focusBySelector = (selector: string, ariaLabel: string) => {
      const element =
        mp3ComponentRef.current?.querySelector<HTMLElement>(selector);
      setViewModeFocusable(element, ariaLabel);
    };

    const focusSelectedRadioValue = (selectedLabel: string) => {
      const container = mp3ComponentRef.current;
      if (!container || !selectedLabel) {
        return;
      }

      const radioCandidate = Array.from(
        container.querySelectorAll<HTMLElement>('[data-testid], label')
      ).find((element) => getControlText(element).includes(selectedLabel));

      const focusTarget =
        radioCandidate?.closest<HTMLElement>('label') ??
        radioCandidate?.closest<HTMLElement>('[data-testid]') ??
        radioCandidate;

      setViewModeFocusable(focusTarget, selectedLabel);
    };

    const applyMP3ViewModeFocus = () => {
      const container = mp3ComponentRef.current;
      if (!container) {
        return;
      }

      container
        .querySelectorAll<HTMLElement>(
          'button, input, select, textarea, a[href], [role="button"], [role="radio"], [role="checkbox"], [role="switch"], [role="combobox"], [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
        )
        .forEach((element) => {
          const isInfoIcon =
            (element.classList.contains('info-icon') ||
              element.classList.contains('info-icon-sm') ||
              element.classList.contains('tooltip-info-icon')) &&
            element.getAttribute('role') === 'button';

          if (isInfoIcon) {
            setViewModeFocusable(element);
            return;
          }

          setViewModeNonFocusable(element);
        });

      if (isBurnRedactionSelected) {
        focusBySelector(
          '[data-testid="mp3components-burn-redactions"]',
          t(
            'productionsAndExports.createNewProduction.productionComponents.mp3.burnRedactions'
          )
        );
      }

      if (isBurnRedactionSelected) {
        focusSelectedRadioValue(
          redactionType === '1'
            ? t(
                'productionsAndExports.createNewProduction.productionComponents.mp3.allRedactionSelected'
              )
            : t(
                'productionsAndExports.createNewProduction.productionComponents.mp3.selectRedactionTags'
              )
        );
      }

      container
        .querySelectorAll<HTMLElement>(
          '[data-testid^="selected-item-tooltip-"]'
        )
        .forEach((selectedValueElement) => {
          suppressDropdownWrapperFocus(selectedValueElement);
          const selectedText = getControlText(selectedValueElement).replace(
            /\s+Selected$/i,
            ''
          );

          if (
            redactionType === '1' &&
            selectedText &&
            Number(redactionStyle?.label) !== 0
          ) {
            setViewModeFocusable(selectedValueElement, selectedText);
          } else {
            setViewModeNonFocusable(selectedValueElement);
          }
        });

      if (redactionType === '0') {
        container
          .querySelectorAll<HTMLElement>(
            '[data-testid^="filtered-selected-tags-chip-"]'
          )
          .forEach((chipElement) => {
            const chipText = getControlText(chipElement);
            if (!chipText) {
              return;
            }

            setViewModeFocusable(chipElement, chipText);
          });
      }

      if (produceMP3LoadFile) {
        focusBySelector(
          '.load-file',
          t(
            'productionsAndExports.createNewProduction.productionComponents.mp3.generateMP3LoadFile'
          )
        );
      }
    };

    const scheduleFocusUpdate = () => {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(applyMP3ViewModeFocus);
    };

    scheduleFocusUpdate();

    const observer = new MutationObserver(scheduleFocusUpdate);
    if (mp3ComponentRef.current) {
      observer.observe(mp3ComponentRef.current, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['tabindex', 'disabled', 'aria-disabled', 'value'],
      });
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      observer.disconnect();
      mp3ViewModeFocusRingTargetsRef.current.forEach(
        ({element, focusHandler, blurHandler}) => {
          element.removeEventListener('focus', focusHandler);
          element.removeEventListener('blur', blurHandler);
          element.style.outline = '';
          element.style.outlineOffset = '';
        }
      );
      mp3ViewModeFocusRingTargetsRef.current = [];
    };
  }, [
    isViewMode,
    isBurnRedactionSelected,
    redactionType,
    redactionStyle,
    TagItem,
    produceMP3LoadFile,
    t,
  ]);

  const handleTagChange = (tagItems: TagItem[]) => {
    if (isViewMode) return;
    setTagItem(tagItems);
    setValue(
      'mP3ComponentModelData.specifiedRedactionTagList',
      tagItems.map((tag) => tag.tagId).join(','),
      {shouldValidate: true}
    );
    setComponentIsDirty(true);
  };

  if (isLoading || !initialMP3Data) return null;

  return (
    <Box className="mp3-container" ref={mp3ComponentRef}>
      <div className="info-container" data-testid={`mp3components-info-text`}>
        <div className="info-icon" data-testid={`mp3components-div`}>
          <InfoOutlineIcon />
        </div>
        <p className="info-text" data-testid={`mp3components-info-text-text`}>
          {t(
            'productionsAndExports.createNewProduction.productionComponents.mp3.infoText'
          )}
        </p>
      </div>
      <Box className="mp3-section">
        <Box className="title-section" display="flex" alignItems="center">
          <Typography
            variant="subtitle1"
            align="left"
            className="mp3-section-title"
            data-testid={`mp3components-redactions`}
          >
            {t(
              'productionsAndExports.createNewProduction.productionComponents.mp3.redactions'
            )}
          </Typography>

          <ArrTooltipWrapper
            closeTooltipOnEscPress
            trigger={
              <InfoIcon
                fontSize="small"
                className="tooltip-info-icon info-icon-sm"
                tabIndex={0}
                role="button"
                aria-label="More information"
              />
            }
          >
            {t(
              'productionsAndExports.createNewProduction.productionComponents.mp3.tooltip'
            )}
          </ArrTooltipWrapper>
        </Box>

        <div
          className="burn-redaction-container"
          data-testid={`mp3components-burn-redactions`}
          tabIndex={isViewMode && isBurnRedactionSelected ? 0 : -1}
          aria-disabled={isViewMode}
          aria-label={t(
            'productionsAndExports.createNewProduction.productionComponents.mp3.burnRedactions'
          )}
        >
          <div
            className="burn-redaction-toggle"
            data-testid={`mp3components-div-btn`}
          >
            <Controller
              name="mP3ComponentModelData.isBurnRedactionSelected"
              control={control}
              render={({field}) => (
                <ArrToggle
                  dataTestId="chkMP3BurnRedactions"
                  value={field.value}
                  onChange={(e, value) => {
                    if (isViewMode) return;
                    setValue(
                      'mP3ComponentModelData.isRedactionByAnnotation',
                      value === true,
                      {shouldValidate: true}
                    );
                    field.onChange(value);
                    setComponentIsDirty(true);
                  }}
                  disabled={isViewMode}
                />
              )}
            />
          </div>
          <Typography
            className="burn-redaction-label mp3-section-label"
            data-testid={`mp3components-burn-redactions-text`}
          >
            {t(
              'productionsAndExports.createNewProduction.productionComponents.mp3.burnRedactions'
            )}
          </Typography>
        </div>
      </Box>
      {isBurnRedactionSelected && (
        <>
          <Box className="mp3-section">
            <Typography
              variant="subtitle1"
              align="left"
              className="mp3-section-title"
              data-testid={`mp3components-specify-redactions`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.mp3.specifyRedactions'
              )}
            </Typography>

            <Box className="mp3-redactions-container">
              <Box className="mp3-redactions-row">
                <Controller
                  name="mP3ComponentModelData.redactionType"
                  control={control}
                  render={({field}) => (
                    <ArrRadio
                      size="small"
                      row={false}
                      className="radio"
                      dataTestId="mP3ComponentModelData.redactionType"
                      name="mP3ComponentModelData.redactionType"
                      value={field.value}
                      data={[
                        {
                          label: t(
                            'productionsAndExports.createNewProduction.productionComponents.mp3.allRedactionSelected'
                          ),
                          value: '1',
                        },
                        {
                          label: t(
                            'productionsAndExports.createNewProduction.productionComponents.mp3.selectRedactionTags'
                          ),
                          value: '0',
                        },
                      ]}
                      onChange={(value) => {
                        if (isViewMode) return;
                        field.onChange(value);
                        setValue(
                          'mP3ComponentModelData.isRedactionByAnnotation',
                          value === '1',
                          {shouldValidate: true}
                        );
                        setComponentIsDirty(true);
                      }}
                      disabled={isViewMode}
                    />
                  )}
                />
              </Box>

              <Box className="mp3-redaction-style-row">
                <Typography
                  variant="subtitle1"
                  className="mp3-redaction-style-label"
                  data-testid={`mp3components-redaction-style`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponents.mp3.redactionStyle'
                  )}
                </Typography>
                <div
                  className="mp3-dropdown"
                  data-testid={`mp3components-div-controller-wrapper`}
                >
                  <Controller
                    name="mP3ComponentModelData.redactionStyle"
                    control={control}
                    render={({field}) => (
                      <ArrSingleSelectDropdown
                        dropdownLabel="Select"
                        dropdownItems={fillerAudioFileList ?? []}
                        selectedValues={field.value as any}
                        sizeVariant="medium"
                        dropdownItemKey="label"
                        dropdownItemDisplay="value"
                        onSelectionChange={(item) => {
                          if (isViewMode) return;
                          field.onChange(item);
                          setComponentIsDirty(true);
                        }}
                        dataTestId="asd-chkMp3RedactionStyle"
                        disabled={isViewMode || redactionType === '0'}
                      />
                    )}
                  />
                </div>
              </Box>
            </Box>

            {redactionType === '0' && (
              <Box className="mp3-tags-box">
                <Controller
                  name="mP3ComponentModelData.redactionTags"
                  control={control}
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  render={({field}) => (
                    <TagsSection
                      headerTitle={t(
                        'productionsAndExports.createNewProduction.productionComponents.mp3.addRedactionTags'
                      )}
                      buttonTitle={t(
                        'productionsAndExports.createNewProduction.productionComponents.mp3.addRedactionTags'
                      )}
                      selectedTags={TagItem}
                      onTagsChange={handleTagChange}
                      tagsTreeData={transformTagTreeData(redactionTagList)}
                      disabled={isViewMode}
                    />
                  )}
                />
              </Box>
            )}
          </Box>
        </>
      )}
      <Divider className="mp3-divider" />
      <Box
        className="load-file"
        tabIndex={isViewMode && produceMP3LoadFile ? 0 : -1}
        aria-disabled={isViewMode}
        aria-label={t(
          'productionsAndExports.createNewProduction.productionComponents.mp3.generateMP3LoadFile'
        )}
      >
        <Controller
          name="mP3ComponentModelData.produceLoadFile"
          control={control}
          render={({field}) => (
            <ArrToggle
              dataTestId="chkMP3ProduceLoadFile"
              value={field.value}
              onChange={(e) => {
                if (isViewMode) return;
                field.onChange(e.target.checked);
              }}
              disabled={isViewMode}
            />
          )}
        />
        <Typography
          className="mp3-section-label"
          data-testid={`mp3components-generate-mp3load-file`}
        >
          {t(
            'productionsAndExports.createNewProduction.productionComponents.mp3.generateMP3LoadFile'
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default Mp3Components;
